import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const SIDEBAR_WIDTH = 260;

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: "bi-speedometer2" },
  // { to: "/statuses", label: "Statuses", icon: "bi-check2-circle" },
  { to: "/bases", label: "Bases", icon: "bi-geo-alt" },
  { to: "/aircrafts", label: "Aircrafts", icon: "bi-airplane" },
  { to: "/airlines", label: "Airlines", icon: "bi-building" },
  { to: "/roles", label: "Roles", icon: "bi-shield-lock" },
  { to: "/seats", label: "Seats", icon: "bi-person-badge" },
  { to: "/users", label: "Users", icon: "bi-people" },
  { to: "/sub-admins", label: "Sub Admins", icon: "bi-person-gear", adminOnly: true },
  { to: "/queries", label: "Queries", icon: "bi-chat-left-text" },
  // { to: "/events", label: "Events", icon: "bi-calendar-event" },
];

const segmentLabels = {
  dashboard: "Dashboard",
  // Fstatuses: "Statuses",
  bases: "Bases",
  aircrafts: "Aircrafts",
  airlines: "Airlines",
  roles: "Roles",
  seats: "Seats",
  users: "Users",
  "sub-admins": "Sub Admins",
  queries: "Queries",
  events: "Events",
  new: "New",
  edit: "Edit",
  detail: "User Detail",
};

function getBreadcrumbs(location) {
  const pathname = location.pathname;
  const state = location.state || {};

  if (pathname.startsWith("/events/detail/") && state.fromUserDetail) {
    return [
      { label: "Dashboard", to: "/dashboard" },
      { label: "Users", to: "/users" },
      { label: "Event Detail", to: null },
    ];
  }

  if (pathname === "/" || pathname === "/dashboard") {
    return [{ label: "Dashboard", to: null }];
  }
  const parts = pathname.split("/").filter(Boolean);
  const items = [{ label: "Dashboard", to: "/dashboard" }];
  let acc = "";
  for (let i = 0; i < parts.length; i++) {
    const seg = parts[i];
    if (/^\d+$/.test(seg)) continue;
    acc += (acc ? "/" : "/") + seg;
    if (seg === "new" || seg === "edit" || seg === "detail") {
      const prev = parts[i - 1];
      const label =
        seg === "detail" && prev === "queries"
          ? "Query Detail"
          : seg === "detail" && prev === "events"
            ? "Event Detail"
            : (segmentLabels[seg] || seg);
      items.push({ label, to: null });
      break;
    }
    const label = segmentLabels[seg] || seg.charAt(0).toUpperCase() + seg.slice(1);
    const isLast = i === parts.length - 1;
    items.push({ label, to: isLast ? null : acc });
  }
  return items;
}

function getHeaderIcon(pathname, items) {
  if (pathname === "/dashboard") return "bi-speedometer2";
  const first = pathname.split("/").filter(Boolean)[0];
  const item = items.find((n) => n.to.slice(1) === first);
  return item ? item.icon : "bi-file-text";
}

export default function AdminLayout({ title, children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const isAdmin = localStorage.getItem("is_admin") === "1";
  const visibleNavItems = navItems.filter((item) => !item.adminOnly || isAdmin);

  const logout = () => {
    setProfileOpen(false);
    localStorage.clear();
    navigate("/login");
  };

  const isActive = (to) => {
    if (to === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname.startsWith(to);
  };

  return (
    <div className="d-flex min-vh-100">
      <aside
        className="skedq-sidebar d-flex flex-column p-3 flex-shrink-0"
        style={{ width: SIDEBAR_WIDTH, minWidth: SIDEBAR_WIDTH }}
      >
        <div className="mb-4 px-1">
          <Link to="/dashboard" className="d-block text-decoration-none">
            <img
              src={`${process.env.PUBLIC_URL}/logo.svg`}
              alt="SkedQ Admin"
              className="w-100"
              style={{ maxHeight: 44, objectFit: "contain", objectPosition: "left" }}
            />
          </Link>
        </div>
        <nav className="nav nav-pills flex-column mb-auto gap-1">
          {visibleNavItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={
                "nav-link d-flex align-items-center gap-2 py-2 " +
                (isActive(item.to) ? "active" : "")
              }
            >
              <i className={`bi ${item.icon}`} style={{ fontSize: "1.1rem" }} />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>
      </aside>

      <main className="skedq-main flex-grow-1 d-flex flex-column min-w-0">
        <header className="skedq-header border-bottom px-4 py-3 d-flex align-items-center justify-content-between flex-shrink-0">
          <div className="d-flex flex-column gap-2">
            {title && (
              <h5 className="mb-0 d-flex align-items-center gap-2">
                <i
                  className={`bi ${getHeaderIcon(location.pathname, visibleNavItems)} text-primary`}
                  style={{ fontSize: "1.25rem" }}
                />
                {title}
              </h5>
            )}
            <nav aria-label="breadcrumb" className={title ? "mt-1" : ""}>
              <ol className="breadcrumb mb-0 py-0 px-0 bg-transparent small">
                {getBreadcrumbs(location).map((item, i) => (
                  <li
                    key={i}
                    className={
                      "breadcrumb-item " + (item.to == null ? "active" : "")
                    }
                  >
                    {item.to != null ? (
                      <Link to={item.to} className="text-decoration-none">
                        {item.label}
                      </Link>
                    ) : (
                      item.label
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          </div>
          <div className="position-relative">
            <button
              type="button"
              className="btn skedq-accent-btn skedq-profile-btn rounded-circle d-flex align-items-center justify-content-center border-0"
              onClick={() => setProfileOpen((o) => !o)}
              aria-expanded={profileOpen}
              aria-haspopup="true"
            >
              <i className="bi bi-person-fill fs-5 text-white" />
            </button>
            {profileOpen && (
              <>
                <div
                  className="position-fixed top-0 start-0 w-100 h-100"
                  style={{ zIndex: 1040 }}
                  onClick={() => setProfileOpen(false)}
                  aria-hidden="true"
                />
                <div
                  className="dropdown-menu show position-absolute end-0 mt-2 shadow"
                  style={{ zIndex: 1050, minWidth: "160px" }}
                >
                  <div className="dropdown-header small text-muted">Account</div>
                  <button
                    type="button"
                    className="dropdown-item d-flex align-items-center gap-2"
                    onClick={logout}
                  >
                    <i className="bi bi-box-arrow-right" />
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </header>
        <div className="p-4 flex-grow-1 overflow-auto">
          <div className="container-fluid">{children}</div>
        </div>
      </main>
    </div>
  );
}

